import React, { useState, useEffect } from 'react';
import '../App.css';

interface CalculatorResults {
  perCollection: number;
  weeklyTotal: number;
  monthlyTotal: number;
  status: 'safe' | 'warning' | 'exceeds';
  messages: string[];
}

interface BloodVolumeCalculatorProps {
  // Controlled mode (optional - for embedding in Phase 5)
  weight?: number;
  volumePerCollection?: number;
  collectionsPerWeek?: number;
  onWeightChange?: (value: number) => void;
  onVolumeChange?: (value: number) => void;
  onFrequencyChange?: (value: number) => void;
  compact?: boolean; // Hide title, reduce padding
}

const BloodVolumeCalculator: React.FC<BloodVolumeCalculatorProps> = ({
  weight: controlledWeight,
  volumePerCollection: controlledVolume,
  collectionsPerWeek: controlledFrequency,
  onWeightChange,
  onVolumeChange,
  onFrequencyChange,
  compact = false
}) => {
  // Internal state (used when NOT controlled)
  const [internalWeight, setInternalWeight] = useState<string>('30');
  const [internalVolume, setInternalVolume] = useState<string>('0.3');
  const [internalFrequency, setInternalFrequency] = useState<string>('1');
  const [results, setResults] = useState<CalculatorResults | null>(null);

  // Use controlled values if provided, otherwise internal state
  const weight = controlledWeight !== undefined ? String(controlledWeight) : internalWeight;
  const volumePerCollection = controlledVolume !== undefined ? String(controlledVolume) : internalVolume;
  const collectionsPerWeek = controlledFrequency !== undefined ? String(controlledFrequency) : internalFrequency;

  // Handle changes - call parent callback if provided
  const handleWeightChange = (value: string) => {
    const num = parseFloat(value);
    if (onWeightChange && !isNaN(num)) {
      onWeightChange(num);
    } else {
      setInternalWeight(value);
    }
  };

  const handleVolumeChange = (value: string) => {
    const num = parseFloat(value);
    if (onVolumeChange && !isNaN(num)) {
      onVolumeChange(num);
    } else {
      setInternalVolume(value);
    }
  };

  const handleFrequencyChange = (value: string) => {
    const num = parseFloat(value);
    if (onFrequencyChange && !isNaN(num)) {
      onFrequencyChange(num);
    } else {
      setInternalFrequency(value);
    }
  };

  // Calculate results whenever inputs change
  useEffect(() => {
    const weightNum = parseFloat(weight);
    const volumeNum = parseFloat(volumePerCollection);
    const collectionsNum = parseFloat(collectionsPerWeek);

    // Only calculate if all inputs are valid positive numbers
    if (weightNum > 0 && volumeNum > 0 && collectionsNum > 0) {
      const perCollection = (volumeNum / weightNum) * 100;
      const weeklyTotal = perCollection * collectionsNum;
      const monthlyTotal = weeklyTotal * 4;

      // Determine status and messages
      let status: 'safe' | 'warning' | 'exceeds' = 'safe';
      const messages: string[] = [];

      // Check limits (Exceeds)
      if (perCollection > 1.0) {
        status = 'exceeds';
        messages.push(`⚠️ Per collection volume EXCEEDS 1% limit (${perCollection.toFixed(2)}%)`);
      }
      if (weeklyTotal > 1.0) {
        status = 'exceeds';
        messages.push(`⚠️ Weekly total EXCEEDS 1% limit (${weeklyTotal.toFixed(2)}%)`);
      }
      if (monthlyTotal > 7.5) {
        status = 'exceeds';
        messages.push(`⚠️ Monthly total EXCEEDS 7.5% limit (${monthlyTotal.toFixed(2)}%)`);
      }

      // Check warnings (Approaching limits) - only if not already exceeding
      if (status === 'safe') {
        if (perCollection >= 0.8) {
          status = 'warning';
          messages.push(`⚠ Per collection volume approaching 1% limit (${perCollection.toFixed(2)}%)`);
        }
        if (weeklyTotal >= 0.8) {
          status = 'warning';
          messages.push(`⚠ Weekly total approaching 1% limit (${weeklyTotal.toFixed(2)}%)`);
        }
        if (monthlyTotal >= 6.0) {
          status = 'warning';
          messages.push(`⚠ Monthly total approaching 7.5% limit (${monthlyTotal.toFixed(2)}%)`);
        }
      }

      setResults({
        perCollection,
        weeklyTotal,
        monthlyTotal,
        status,
        messages
      });
    } else {
      setResults(null);
    }
  }, [weight, volumePerCollection, collectionsPerWeek]);

  return (
    <div className={`blood-volume-calculator ${compact ? 'compact' : ''}`}>
      {!compact && <h4>Blood Collection Volume Calculator</h4>}

      <div className="calculator-inputs">
        <div className="calculator-input-group">
          <label htmlFor="animal-weight">Animal Weight (g):</label>
          <input
            id="animal-weight"
            type="number"
            step="0.1"
            min="0"
            value={weight}
            onChange={(e) => handleWeightChange(e.target.value)}
            placeholder="e.g., 30"
          />
        </div>

        <div className="calculator-input-group">
          <label htmlFor="volume-per-collection">Volume per Collection (mL):</label>
          <input
            id="volume-per-collection"
            type="number"
            step="0.01"
            min="0"
            value={volumePerCollection}
            onChange={(e) => handleVolumeChange(e.target.value)}
            placeholder="e.g., 0.3"
          />
        </div>

        <div className="calculator-input-group">
          <label htmlFor="collections-per-week">Collections per Week:</label>
          <input
            id="collections-per-week"
            type="number"
            step="1"
            min="0"
            value={collectionsPerWeek}
            onChange={(e) => handleFrequencyChange(e.target.value)}
            placeholder="e.g., 1"
          />
        </div>
      </div>

      {results && (
        <div className={`calculator-results calculator-results-${results.status}`}>
          <h5>Results:</h5>
          <div className="results-grid">
            <div className="result-item">
              <span className="result-label">Per Collection:</span>
              <span className="result-value">{results.perCollection.toFixed(2)}% of body weight</span>
            </div>
            <div className="result-item">
              <span className="result-label">Weekly Total:</span>
              <span className="result-value">{results.weeklyTotal.toFixed(2)}% of body weight</span>
            </div>
            <div className="result-item">
              <span className="result-label">Monthly Total:</span>
              <span className="result-value">{results.monthlyTotal.toFixed(2)}% of body weight (assuming 4 weeks)</span>
            </div>
          </div>

          {results.messages.length > 0 ? (
            <div className="calculator-messages">
              {results.messages.map((msg, idx) => (
                <div key={idx} className="calculator-message">{msg}</div>
              ))}
            </div>
          ) : (
            <div className="calculator-status-safe">
              ✓ All values are within institutional limits.
            </div>
          )}

          <div className="calculator-limits-reference">
            <strong>Institutional Limits:</strong> Single collection ≤1% body weight | Weekly ≤1% | Monthly ≤7.5%
          </div>
        </div>
      )}
    </div>
  );
};

export default BloodVolumeCalculator;
